import { Injectable } from '@angular/core';
import * as html2pdf from 'html2pdf.js';

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorService {

  constructor() { }

  /**
   * @param captureWidth optional width (px) of the content. html2canvas renders at the browser window's width and clips anything wider,
   * so content that is wider than the window (wide report tables) must pass its own width here to be captured completely.
   */
  private getWorker(element: HTMLElement, filename: string, orientation: 'portrait' | 'landscape' = 'portrait', captureWidth?: number) {
    const opt = {
      margin: 0.5,
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, ...(captureWidth ? { windowWidth: captureWidth, width: captureWidth } : {}) },
      jsPDF: { unit: 'mm', format: 'letter', orientation: orientation },
      pagebreak: { 
        mode: ['css', 'legacy'],
        avoid: ['tr', 'thead', 'th'],
        before: '.before-page-break', // Can use a custom class
        after: '.after-page-break'    // Can use a custom class



      }
    };

    const worker = (html2pdf as any).default || html2pdf;
    let job = worker().from(element).set(opt);

    if (captureWidth) {
      // html2pdf's page-break plugin measures the page in CSS px of the page-width container (about 812px for letter landscape), but
      // the canvas is later sliced into pages of (captured width x page ratio), i.e. captureWidth x 0.772 px when the content is laid
      // out wider than the page. Without this correction every break (css/legacy/avoid) is placed at the wrong offset, so sections do
      // not start on a page boundary and table rows/headers are split. Use the same page height the slicer uses (canvas px / scale).
      const scale = 2; // keep in sync with html2canvas.scale above
      job = job.then(function (this: any) {
        const inner = this.prop.pageSize?.inner;
        if (inner?.px && inner.ratio) {
          inner.px.height = Math.floor(captureWidth * scale * inner.ratio) / scale;
        }
      });
    }

    return job;
  }

  async generateBlob(element: HTMLElement, filename: string, orientation: 'portrait' | 'landscape' = 'portrait'): Promise<Blob> {
    const originalSpacing = element.style.letterSpacing;
    element.style.letterSpacing = '0.5px';
    try {
      return await this.getWorker(element, filename, orientation).output('blob');
    } finally {
      element.style.letterSpacing = originalSpacing;
    }
  }

  async savePdf(element: HTMLElement, filename: string, orientation: 'portrait' | 'landscape'): Promise<void> {
    const originalSpacing = element.style.letterSpacing;
    element.style.letterSpacing = '0.5px';
    try {
      const blob = await this.getWorker(element, filename, orientation).output('blob');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      element.style.letterSpacing = originalSpacing;
    }
  }
  
  async openPdf(element: HTMLElement, filename: string, orientation: 'portrait' | 'landscape' = 'portrait', windowRef?: Window | null, captureWidth?: number): Promise<void> {
    const originalSpacing = element.style.letterSpacing;
    element.style.letterSpacing = '0.5px';
    try {
      const blob = await this.getWorker(element, filename, orientation, captureWidth).output('blob');
      const url = URL.createObjectURL(blob);
      if (windowRef) {
        windowRef.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } finally {
      element.style.letterSpacing = originalSpacing;
    }
  }
}